import { Project } from '@griply/assets';
import { EntityLinks, GoalType } from '@griply/types';
import { isGoalArchived } from '@griply/utils';
import React, { useMemo } from 'react';
import {
  Archive,
  CheckThick,
  ChevronLeft,
  IconFormIcons,
  Target,
} from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { DataType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import {
  goalInboxID,
  overdueSectionID,
  unlinkedPlannedTasksID,
} from 'shared/types/orderable-section';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { Deadline } from './deadline';
import * as Styled from './goal-item-card.style';

enum Illustration {
  Archived = 'archived',
  CompletionPercentage = 'completionPercentage',
  Icon = 'icon',
  TypeIcon = 'typeIcon',
}

export type GoalItemCardProps = {
  goal: Goal;

  lifeAreas: LifeArea[];
  goals: Goal[];
  // Caller-supplied: a UI component must not fetch — without it a migrated goal
  // resolves no parent and never inherits its parent's archived state.
  links: EntityLinks;
  childCount?: number;

  onCollapse?: (e: React.MouseEvent) => void;
  collapsed?: boolean;
  isGrabbable?: boolean;
  isGrabbed?: boolean;
  // Timeline: only show the flag + deadline, and only when both dates are set.
  deadlineOnly?: boolean;
  // Show a color-coded goal/project type icon instead of the progress ring.
  showTypeIcon?: boolean;
};

export const GoalItemCard: React.FC<GoalItemCardProps> = ({
  goal,
  lifeAreas,
  goals,
  links,
  childCount,
  onCollapse,
  collapsed,
  isGrabbable,
  isGrabbed,
  deadlineOnly,
  showTypeIcon,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!goal.frozenAt,
  });

  const _onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCollapse?.(e);
  };

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas, links }),
    [goal.id, goals, lifeAreas, links],
  );

  const isArchived = useMemo(
    () => isGoalArchived(goal, goals, { links }),
    [goal, goals, links],
  );

  const taskCounts = useTaskCounts();

  const completionPercentage = useMemo(() => {
    const { start, end, current } = getGoalCompletion(goal, {
      goalProgressCounts: taskCounts?.byGoalProgress,
    });
    return end !== Infinity
      ? getCompletionPercentage({ start, end, current })
      : undefined;
  }, [goal, taskCounts?.byGoalProgress]);

  const isOverdueOrInbox = [goalInboxID, overdueSectionID].includes(goal.id);

  const isFakeGoal = isOverdueOrInbox || unlinkedPlannedTasksID === goal.id;

  const illustrationToDisplay = isArchived
    ? Illustration.Archived
    : isFakeGoal
      ? Illustration.Icon
      : showTypeIcon
        ? Illustration.TypeIcon
        : Illustration.CompletionPercentage;

  return (
    <>
      <Styled.Container
        $isGrabbable={!!isGrabbable}
        $isGrabbed={!!isGrabbed}
        onClick={_onClick}
        onContextMenu={openMenu}
        data-component-type={DataType.Goal}
        data-id={goal.id}
        data-title={goal.name}
      >
        <Styled.Content $completed={!!goal.completedAt} $archived={isArchived}>
          <Styled.IllustrationContainer
            $color={
              illustrationToDisplay === Illustration.Icon ||
              illustrationToDisplay === Illustration.Archived
                ? color
                : undefined
            }
          >
            {illustrationToDisplay === Illustration.CompletionPercentage && (
              <Styled.ProgressContainer $color={color}>
                <CircleProgress
                  percentage={completionPercentage}
                  hidePercentage
                  isSmall
                />
              </Styled.ProgressContainer>
            )}

            {illustrationToDisplay === Illustration.TypeIcon && (
              <Styled.TypeIconContainer $color={color}>
                <Icon
                  icon={goal.type === GoalType.Project ? Project : Target}
                />
              </Styled.TypeIconContainer>
            )}

            {[Illustration.Icon, Illustration.Archived].includes(
              illustrationToDisplay,
            ) && (
              <Styled.IconContainer
                $error={goal.id === overdueSectionID}
                $noBackground={
                  isFakeGoal || illustrationToDisplay === Illustration.Archived
                }
              >
                <Icon
                  icon={
                    illustrationToDisplay === Illustration.Archived
                      ? Archive
                      : IconFormIcons[goal.iconName!] ?? Target
                  }
                />
              </Styled.IconContainer>
            )}

            {!!goal.completedAt && (
              <Styled.CompletedContainer>
                <Icon icon={CheckThick} />
              </Styled.CompletedContainer>
            )}
          </Styled.IllustrationContainer>

          <Styled.TitleContainer
            $completed={!!goal.completedAt}
            to={isFakeGoal ? '' : getGoalDetailUrl(goal)}
            as={isFakeGoal ? 'span' : undefined}
          >
            <Styled.Title $isH6={isOverdueOrInbox}>
              {goal.name}
              {isOverdueOrInbox && !!childCount && (
                <Styled.Badge>{childCount}</Styled.Badge>
              )}
            </Styled.Title>
            {!isFakeGoal && (
              <Styled.ChevronContainer>
                <Icon icon={ChevronLeft} />
              </Styled.ChevronContainer>
            )}
          </Styled.TitleContainer>
        </Styled.Content>

        <Styled.MetaContainer>
          {!goal.completedAt && !isArchived && (
            <Deadline goal={goal} deadlineOnly={deadlineOnly} />
          )}

          {!!onCollapse && (
            <IconButton
              size={Sizes.Small}
              icon={ChevronLeft}
              rotation={collapsed ? -90 : 0}
              color={Colors.Default}
              as="span"
            />
          )}
        </Styled.MetaContainer>
      </Styled.Container>

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          onClose={closeMenu}
          location={menuPosition}
        />
      )}
    </>
  );
};
